import { select } from "d3-selection";
import { boundingExtent } from "ol/extent";
import OlGeomPoint from "ol/geom/Point";
import OlGeomPolygon from "ol/geom/Polygon";
import OlGeomLineString from "ol/geom/LineString";
import Renderer from "ol/render/canvas/Immediate";
import { create as createTransform } from "ol/transform";
import OlStyleParser from "geostyler-openlayers-parser";
import OlStyle from "ol/style/Style";
import { Icon } from "ol/style";
/**
 * A class that can be used to render svg legends.
 */
export class LegendRenderer {
    /**
     * Constructs a new legend renderer.
     * @param {LegendsConfiguration} config the legend configuration
     */
    constructor(config) {
        this.config = null;
        this.config = config;
        if (!this.config.iconSize) {
            this.config.iconSize = [45, 30];
        }
    }
    /**
     * Constructs a legend configuration from a geostyler style object.
     * @param {Style} style a geostyler style
     * @param {boolean} stylename if the stylename should be used or only the rule name
     */
    extractConfigFromStyle(style, stylename) {
        const config = {
            items: [],
            title: "",
        };
        style.rules.forEach((rule) => {
            if (stylename) {
                config.items.push({
                    title: rule.name || style.name || "",
                    rule,
                });
            }
            else {
                config.items.push({
                    title: rule.name || "",
                    rule,
                });
            }
        });
        return config;
    }
    /**
     * Renders a single legend item.
     * @param {Selection} container the container to append the legend item to
     * @param {LegendItemConfiguration} item configuration of the legend item
     * @param {[number, number]} position the current position
     */
    renderLegendItem(item, container, position) {
        if (item.rule) {
            const { hideRect, maxColumnHeight, maxColumnWidth } = this.config;
            container = container.append("g").attr("class", "legend-item").attr("title", item.title);
            const img = this.getRuleIcon(item.rule);
            return img
                .then((uri) => {
                if (!hideRect) {
                    container
                        .append("rect")
                        .attr("x", position[0] + 1)
                        .attr("y", position[1])
                        .attr("width", this.config.iconSize[0])
                        .attr("height", this.config.iconSize[1])
                        .style("fill-opacity", 0)
                        .style("stroke", "black");
                }
                container
                    .append("image")
                    .attr("x", position[0] + 1)
                    .attr("y", position[1])
                    .attr("width", this.config.iconSize[0])
                    .attr("height", this.config.iconSize[1])
                    .attr("href", uri);
                container
                    .append("text")
                    .text(item.title)
                    .attr("x", position[0] + this.config.iconSize[0] + 5)
                    .attr("y", position[1] + 20);
                position[1] += this.config.iconSize[1];
                if (maxColumnHeight && position[1] + this.config.iconSize[1] + 5 >= maxColumnHeight) {
                    position[1] = 0;
                    position[0] += maxColumnWidth;
                }
            })
                .catch((error) => console.log(error));
        }
        return undefined;
    }
    /**
     * Shortens the labels if they overflow.
     * @param {Selection} nodes the legend item group nodes
     * @param {number} maxWidth the maximum column width
     */
    shortenLabels(nodes, maxWidth) {
        nodes.each(function () {
            const node = select(this);
            const text = node.select("text");
            if (!(node.node() instanceof SVGElement)) {
                return;
            }
            const elem = node.node();
            let width = elem.getBoundingClientRect().width;
            let adapted = false;
            while (width > maxWidth) {
                let str = text.text();
                str = str.substring(0, str.length - 1);
                text.text(str);
                width = elem.getBoundingClientRect().width;
                adapted = true;
            }
            if (adapted) {
                let str = text.text();
                str = str.substring(0, str.length - 3);
                text.text(str + "...");
            }
        });
    }
    /**
     * Constructs a geometry for rendering a specific symbolizer.
     * @param {Symbolizer} symbolizer the symbolizer object
     */
    getGeometryForSymbolizer(symbolizer) {
        const kind = symbolizer.kind;
        switch (kind) {
            case "Mark":
            case "Icon":
                return new OlGeomPoint([this.config.iconSize[0] / 2, this.config.iconSize[1] / 2]);
            case "Text":
                return new OlGeomPoint([this.config.iconSize[0] / 2, this.config.iconSize[1] / 2]);
            case "Fill":
                return new OlGeomPolygon([
                    [
                        [3, 3],
                        [this.config.iconSize[0] - 3, 3],
                        [this.config.iconSize[0] - 3, this.config.iconSize[1] - 3],
                        [3, this.config.iconSize[1] - 3],
                        [3, 3],
                    ],
                ]);
            case "Line":
                return new OlGeomLineString([
                    [this.config.iconSize[0] / 6, this.config.iconSize[1] / 6],
                    [this.config.iconSize[0] / 3, (this.config.iconSize[1] / 3) * 2],
                    [this.config.iconSize[0] / 2, this.config.iconSize[1] / 2],
                    [(this.config.iconSize[0] / 6) * 5, (this.config.iconSize[1] / 6) * 5],
                ]);
            default:
                return new OlGeomPoint([this.config.iconSize[0] / 2, this.config.iconSize[1] / 2]);
        }
    }
    /**
     * Returns a promise resolving to a data uri with the appropriate rule icon.
     * @param {Object} rule the geostyler rule
     */
    getRuleIcon(rule) {
        const canvas = document.createElement("canvas");
        canvas.setAttribute("width", `${this.config.iconSize[0]}`);
        canvas.setAttribute("height", `${this.config.iconSize[1]}`);
        const extent = boundingExtent([
            [0, 0],
            [this.config.iconSize[0], this.config.iconSize[1]],
        ]);
        let pixelRatio = 1;
        const context = canvas.getContext("2d");
        const transform = createTransform();
        let renderer = new Renderer(context, pixelRatio, extent, transform, 0);
        let geoms = [];
        rule.symbolizers.forEach((symbolizer) => geoms.push(this.getGeometryForSymbolizer(symbolizer)));
        geoms = geoms.reverse();
        const styleParser = new OlStyleParser();
        rule.symbolizers.forEach((symbolizer) => {
            if (symbolizer.kind == "Mark") {
                if (symbolizer.wellKnownName == "circle") {
                    if (typeof symbolizer.radius === "number" && symbolizer.radius > 8) {
                        symbolizer.radius = 8;
                    }
                }
            }
        });
        const style = {
            name: "",
            rules: [
                {
                    name: "",
                    symbolizers: rule.symbolizers,
                },
            ],
        };
        const promise = new Promise((resolve, reject) => {
            styleParser
                .writeStyle(style)
                .then(({ output: olStyle }) => {
                const mergedOlStyle = this.mergeOlStyles(olStyle);
                const icon = mergedOlStyle.getImage();
                if (icon instanceof Icon) {
                    const img = new Image();
                    img.src = icon.getSrc();
                    img.crossOrigin = "anonymous";
                    img.onload = () => {
                        mergedOlStyle.setImage(new Icon({
                            img: img,
                        }));
                        const width_ratio = (this.config.iconSize[0] - 6) / img.width;
                        const height_ratio = (this.config.iconSize[1] - 6) / img.height;
                        if (width_ratio < height_ratio) {
                            pixelRatio = width_ratio;
                        }
                        else {
                            pixelRatio = height_ratio;
                        }
                        renderer = new Renderer(context, pixelRatio, extent, transform, 0);
                        this.rendergeom(geoms, mergedOlStyle, renderer, resolve, canvas);
                    };
                }
                else {
                    this.rendergeom(geoms, mergedOlStyle, renderer, resolve, canvas);
                }
            })
                .catch((error) => {
                console.log(error);
                reject();
            });
        });
        return promise;
    }
    rendergeom(geoms, olStyle, renderer, resolve, canvas) {
        renderer.setStyle(olStyle);
        geoms.forEach((geom) => renderer.drawGeometry(geom));
        resolve(canvas.toDataURL("image/png"));
    }
    mergeOlStyles(olStyle) {
        if (olStyle instanceof Array) {
            let imagestyle = null;
            let strokestyle = null;
            let fillstyle = null;
            for (const style of olStyle) {
                if (style.getImage())
                    imagestyle = style.getImage();
                if (style.getStroke())
                    strokestyle = style.getStroke();
                if (style.getFill())
                    fillstyle = style.getFill();
            }
            return new OlStyle({
                image: imagestyle,
                stroke: strokestyle,
                fill: fillstyle,
            });
        }
        else if (typeof olStyle === "function") {
            return undefined;
        }
        return olStyle;
    }
    /**
     * Render a single legend.
     * @param {LegendConfiguration} config the legend config
     * @param {Selection} svg the root node
     * @param {[number, number]} position the current position
     */
    renderLegend(config, svg, position) {
        const container = svg.append("g");
        if (this.config.overflow !== "auto" && position[0] !== 0) {
            const legendHeight = config.items.length * (this.config.iconSize[1] + 5) + 20;
            if (legendHeight + position[1] > this.config.maxColumnHeight) {
                position[0] += this.config.maxColumnWidth;
                position[1] = 0;
            }
        }
        if (config.title) {
            container
                .append("text")
                .text(config.title)
                .attr("class", "legend-title")
                .attr("text-anchor", "start")
                .attr("dy", "1em")
                .attr("dx", position[0]);
            position[1] += 20;
        }
        return config.items
            .reduce((cur, item) => {
            return cur.then(() => this.renderLegendItem(item, svg, position)).catch((error) => console.log(error));
        }, Promise.resolve())
            .catch((error) => console.log(error));
    }
    /**
     * Renders the configured legend.
     * @param {HTMLElement} parent a node to append the svg to
     * @return {Promise<void>} a promise resolving once the legend has finished rendering
     */
    render(parent) {
        const { styles, configs, size: [width, height], } = this.config;
        const legends = [];
        if (styles) {
            styles.forEach((style) => legends.push(this.extractConfigFromStyle(style, true)));
        }
        if (configs) {
            // eslint-disable-next-line
            legends.unshift.apply(legends, configs);
        }
        const svg = select(parent)
            .append("svg")
            .attr("viewBox", `0 0 ${width} ${height}`)
            .attr("top", 0)
            .attr("left", 0)
            .attr("width", width)
            .attr("height", height);
        const position = [0, 0];
        const promise = legends
            .reduce((cur, legend) => {
            return cur.then(() => this.renderLegend(legend, svg, position)).catch((error) => console.log(error));
        }, Promise.resolve())
            .catch((error) => console.log(error));
        return promise
            .then(() => {
            const nodes = svg.selectAll("g.legend-item");
            this.shortenLabels(nodes, this.config.maxColumnWidth);
            if (!this.config.maxColumnHeight) {
                svg.attr("viewBox", `0 0 ${width} ${position[1]}`).attr("height", position[1]);
            }
        })
            .catch((error) => console.log(error));
    }
    render2arr() {
        const legends = [];
        if (this.config.styles) {
            this.config.styles.forEach((style) => legends.push(this.extractConfigFromStyle(style, false)));
        }
        if (this.config.configs) {
            // eslint-disable-next-line
            legends.unshift.apply(legends, this.config.configs);
        }
        return new Promise((resolve, reject) => {
            const arr = [];
            legends[0].items
                .reduce((cur, item) => {
                return cur.then(() => {
                    const img = this.getRuleIcon(item.rule);
                    return img.then((uri) => {
                        arr.push([item.title, uri]);
                    });
                });
            }, Promise.resolve())
                .then((a) => {
                resolve(arr);
            });
        });
    }
}
export default LegendRenderer;
//# sourceMappingURL=LegendRenderer.js.map